import React, { useMemo } from "react";
import { IDamageINvestigationFollowUp } from "../models/prException";
import { EOsdType } from "../constants";

interface ASInvestigationTemplateProps {
  values?: IDamageINvestigationFollowUp;
  osdNumber: number;
  osdType?: string;
  isFollowUp?: boolean; // Default value for isFollowUp
}

const PRInvestigationTemplate: React.FC<ASInvestigationTemplateProps> = ({
  values = {},
  osdNumber,
  osdType,
  isFollowUp = false,
}) => {
  const memoizedValues = useMemo(() => values, [values]);

  const isRF = osdType === EOsdType.rf;
  const isDD = osdType === EOsdType.dd;

  const renderTitle = (title: string) => (
    <div style={styles.sectionContainer}>
      <div style={styles.sectionTitle}>{title}</div>
    </div>
  );

  const returnYesNo = (value: keyof IDamageINvestigationFollowUp) => {
    if (memoizedValues?.[value] === "Y") return "Yes";
    return "No";
  };

  const renderSection = (title: string, value?: string) => (
    <div style={styles.sectionContainer}>
      <div style={styles.sectionTitle}>{title}</div>
      {
        <div
          style={{
            background: "#f5f7fb",
            padding: "10px",
            border: "1px solid #b5b6b9",
            wordWrap: "break-word",
          }}
        >
          {value}
        </div>
      }
    </div>
  );

  const renderOptionSection = (
    title: string,
    value?: string,
    sValue: string = ""
  ) => (
    <div style={styles.sectionContainer}>
      <div style={styles.sectionTitle}>{title}</div>
      {
        <div
          style={{
            width: "15vw",
            display: "inline-block",
            verticalAlign: "top",
          }}
        >
          {value}
        </div>
      }
      {sValue && (
        <div
          style={{
            width: "75%",
            margin: "0 0 0 0",
            display: "inline-block",
            background: "#f5f7fb",
            padding: "10px",
            border: "1px solid #b5b6b9",
            wordWrap: "break-word",
          }}
        >
          {sValue}
        </div>
      )}
    </div>
  );

  const headingTitle = {
    [EOsdType.pr]: "Partial Refusal ",
    [EOsdType.rd]: "Refused Damage ",
    [EOsdType.rf]: "Arbitrary Refusal ",
    [EOsdType.dd]: "Delivered Damages ",
  };

  return (
    <div id="as-in-day3">
      <div
        style={{
          width: "100vw",
          background: "#f5f7fb",
          padding: "10px 15px",
          fontSize: "18px",
          fontWeight: "bold",
          textTransform: "uppercase",
        }}
      >
        {headingTitle[osdType as keyof typeof headingTitle]}{" "}
        {(isFollowUp || isDD) ? "Follow up" : "Investigation"}{" "}
        <span
          style={{
            background: "#f5f5f5",
            border: "1px solid #d4d4d4",
            padding: "5px 15px",
            borderRadius: "20px",
            fontSize: "14px",
            fontWeight: "bold",
            marginLeft: "10px",
          }}
        >
          #{osdNumber}
        </span>
      </div>
      <div style={styles.container}>
        {!isFollowUp && (
          <>
            {renderOptionSection(
              "Was the correct delivery status entered?",
              returnYesNo("wasCorrectDeliveryStatusEntered")
            )}
            {renderOptionSection(
              "Confirm the delivery code matches the ADE",
              returnYesNo("confirmDeliveryCodeMatchesADE")
            )}
            {renderOptionSection(
              "Confirm the driver took valid EP’s",
              returnYesNo("confirmDriverTookValidEPs")
            )}
            {!isDD && (<> 
            {renderOptionSection(
              "Check for special instructions on the body of bill, check the BOL, and 78/23 for the shipper, consignee and 3rd party",
              memoizedValues.checkSpecialInstructions === "Y" ? "Yes" : "No",
              memoizedValues.specialInstructionsDetails
            )}
            {renderTitle(
              "Check the verbiage in the ADE to describe the extent of damage and non-damaged items"
            )}
            {!isRF &&
              renderOptionSection(
                "Is all of the product damaged?",
                memoizedValues.isAllProductDamaged === "Y" ? "Yes" : "No"
              )}
            {!isRF && memoizedValues.isAllProductDamaged === "Y" && (
              <>
                {renderSection(
                  "What does the shipment consist of?",
                  memoizedValues.shipmentConsistsOf
                )}
                {renderSection(
                  "What is the product?",
                  memoizedValues.productDescription
                )}
                {renderSection(
                  "What kind of package?",
                  memoizedValues.packageType
                )}
                {renderSection(
                  "Is there damage to the packaging?",
                  memoizedValues.isPackageDamaged
                )}
                {renderSection(
                  "Is the actual product damaged, if so, how bad?",
                  memoizedValues.isProductDamaged
                )}
                {renderSection(
                  "Is any of the actual product NOT damaged, if so how much?",
                  memoizedValues.isProductNotDamaged
                )}
                {renderSection(
                  "Is the package repairable?",
                  memoizedValues.isPackageRepairable
                )}
                {renderSection(
                  "Are there any valid pictures, of the freight and the damage, in image?",
                  memoizedValues.areValidPicturesAvailable
                )}
              </>
            )}
            {memoizedValues.isAllProductDamaged === "N" && !isRF && (
              <>
                {renderOptionSection(
                  "Can the product recouped?",
                  memoizedValues.canProductBeRecouped === "Y" ? "Yes" : "No"
                )}
              </>
            )}
            {!isRF && memoizedValues.canProductBeRecouped === "N" && (
              <>
                {renderSection(
                  "Provide a reason why it cannot be recouped",
                  memoizedValues.reasonWhyCannotBeRecouped
                )}
              </>
            )}
            {(memoizedValues.isAllProductDamaged === "N" || isRF) && (
              <>
                {renderOptionSection(
                  "Will the consignee accept delivery? ",
                  memoizedValues.willConsigneeAcceptRedelivery === "Y"
                    ? "Yes"
                    : "No"
                )}
              </>
            )}
            {memoizedValues.willConsigneeAcceptRedelivery === "Y" && (
              <>
                {renderSection("Consignee Name", memoizedValues.consigneeName)}
                {renderSection("Phone Number", memoizedValues.consigneePhone)}
                {renderSection("E-mail Address", memoizedValues.consigneeEmail)}
              </>
            )}
            {renderOptionSection(
              "Check for photos of the product, either EP's or Photos taken after the recoup ",
              memoizedValues.checkPhotos === "Y" ? "Yes" : "No"
            )}
            {renderOptionSection(
              "Updated F18 notes with who you contacted (name, email and/or phone number) ",
              memoizedValues.updatedF18Notes === "Y" ? "Yes" : "No"
            )}
            </>)}
            {renderSection("F18 notes", memoizedValues.f18Note)}
          </>
        )}

        {isFollowUp && (
          <>
            {renderOptionSection(
              "Does the shipper want the freight back?",
              memoizedValues.doesShipperWantFreightBack === "Y" ? "Yes" : "No",
              memoizedValues.freightBackDetails
            )}
            {memoizedValues.doesShipperWantFreightBack === "Y" &&
              osdType !== EOsdType.pr && (
                <>
                  {renderOptionSection(
                    "Clear your delivery exception pro number  on the 86 screen to show status RCN ",
                    memoizedValues.clearShipmentStatusRCN === "Y" ? "Yes" : "No"
                  )}
                </>
              )}
            {!isRF && memoizedValues.doesShipperWantFreightBack === "N" && (
              <>
                {renderSection(
                  "User must select a value range",
                  memoizedValues.valueRangeFollowUp
                )}
              </>
            )}
            {memoizedValues.doesShipperWantFreightBack === "N" &&
              renderOptionSection(
                "Was return authorization received? ",
                memoizedValues.wasReturnAuthorizationReceived === "Y"
                  ? "Yes"
                  : "No"
              )}
            {renderSection("F18 notes", memoizedValues.f18Note2)}
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  header: {
    width: "100vw",
    background: "#f5f7fb",
    padding: "10px 15px",
    fontSize: "18px",
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  osdBadge: {
    background: "#f5f5f5",
    border: "1px solid #d4d4d4",
    padding: "5px 15px",
    borderRadius: "20px",
    fontSize: "14px",
    fontWeight: "bold",
    marginLeft: "10px",
  },
  container: {
    padding: "20px",
  },
  sectionContainer: {
    marginBottom: "15px",
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: "5px",
  },
  sectionContent: {
    background: "#f5f7fb",
    padding: "10px",
    border: "1px solid #b5b6b9",
    wordWrap: "break-word",
  },
};

export default PRInvestigationTemplate;
